import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Play, Pause, SkipBack, SkipForward, ArrowLeft, Download, Loader2, Volume2, ZoomIn, ZoomOut, Trash2, Music, Plus, ChevronDown, ChevronUp, Sparkles, History, BarChart3, Scissors, Wand2, FileText, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import ExportProgressModal from '../components/ExportProgressModal';
import StemSeparation from '../components/StemSeparation';
import TrackAnalytics from '../components/TrackAnalytics';

export default function Editor() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsRef = useRef(null);
  const mainContentRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState('auto');

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [regions, setRegions] = useState([]);
  const [audioMode, setAudioMode] = useState('vocal'); // 'vocal' or 'original'
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [lyricsExpanded, setLyricsExpanded] = useState(true);
  const [newRegionTime, setNewRegionTime] = useState('0:00');
  const [newRegionWord, setNewRegionWord] = useState('');
  const [beepFrequency, setBeepFrequency] = useState(1000);
  const [beepVolume, setBeepVolume] = useState(100);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedWordIndex, setSelectedWordIndex] = useState(-1);
  const [selectedWordTiming, setSelectedWordTiming] = useState(null); // Store precise timing when clicking a lyric
  const [stemsExpanded, setStemsExpanded] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [userMinutes, setUserMinutes] = useState(0);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedLyrics, setEnhancedLyrics] = useState(null);
  const [lyricsAutoScroll, setLyricsAutoScroll] = useState(true);
  const [showPasteLyricsDialog, setShowPasteLyricsDialog] = useState(false);
  const [customLyrics, setCustomLyrics] = useState('');
  const [editingInfo, setEditingInfo] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editArtist, setEditArtist] = useState('');
  const lyricsContainerRef = useRef(null);
  const currentWordRef = useRef(null);

  useEffect(() => {
    // Scroll to top when navigating to this page
    window.scrollTo(0, 0);
    
    fetchProject();
    fetchUserMinutes();
    
    // Cleanup on unmount
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    };
  }, [sessionId]);

  useEffect(() => {
    if (session && session.files?.vocal_url && waveformRef.current && !wavesurferRef.current) {
      initWaveSurfer();
    }
  }, [session]);

  useEffect(() => {
    if (!mainContentRef.current) return;
    
    const updateSidebarHeight = () => {
      if (mainContentRef.current) {
        const height = mainContentRef.current.offsetHeight;
        setSidebarHeight(`${height}px`);
      }
    };

    const resizeObserver = new ResizeObserver(updateSidebarHeight);
    resizeObserver.observe(mainContentRef.current);
    
    updateSidebarHeight();

    return () => resizeObserver.disconnect();
  }, [session, stemsExpanded, analyticsExpanded, lyricsExpanded]);

  // Auto-scroll lyrics to current word
  useEffect(() => {
    if (lyricsAutoScroll && currentWordRef.current && lyricsContainerRef.current) {
      currentWordRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentTime, lyricsAutoScroll]);

  const fetchProject = async () => {
    try {
      const response = await api.get(`/sessions/${sessionId}`);
      setSession(response.data);
      setRegions(response.data.regions || []);
      setBeepFrequency(response.data.beep_frequency || 1000);
      setBeepVolume((response.data.beep_volume || 1.0) * 100);
      setEditTitle(response.data.title || response.data.filename || '');
      setEditArtist(response.data.artist || '');
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load session');
      navigate('/dashboard');
    }
  };

  const handleSaveInfo = async () => {
    try {
      await api.put(`/sessions/${sessionId}`, {
        title: editTitle,
        artist: editArtist
      });
      setSession(prev => ({ ...prev, title: editTitle, artist: editArtist }));
      setEditingInfo(false);
      toast.success('Song info updated');
    } catch (error) {
      toast.error('Failed to update song info');
    }
  };

  const handleCancelEdit = () => {
    setEditTitle(session?.title || session?.filename || '');
    setEditArtist(session?.artist || '');
    setEditingInfo(false);
  };

  const fetchUserMinutes = async () => {
    try {
      const response = await api.get('/user/profile');
      setUserMinutes(response.data.minutes || 0);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const initWaveSurfer = () => {
    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: 'hsl(188, 50%, 28%)',
      progressColor: 'hsl(188, 85%, 56%)',
      cursorColor: 'hsl(210, 20%, 96%)',
      height: 180,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
    });

    regionsRef.current = ws.registerPlugin(RegionsPlugin.create());

    ws.on('ready', () => {
      setDuration(ws.getDuration());
      
      // Add regions for detected curses
      regions.forEach((region) => {
        addRegion(region);
      });
    });

    ws.on('audioprocess', () => {
      const time = ws.getCurrentTime();
      setCurrentTime(time);
      
      // Update current word for lyrics highlighting
      if (session?.transcription?.words) {
        const words = session.transcription.words;
        const index = words.findIndex((word, i) => {
          const nextWord = words[i + 1];
          return time >= word.start && (!nextWord || time < nextWord.start);
        });
        setCurrentWordIndex(index);
      }
    });

    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));

    // Load audio
    ws.load(session.files.vocal_url);

    wavesurferRef.current = ws;
  };

  const addRegion = (regionData) => {
    if (!regionsRef.current) return;

    // Yellow for custom regions, orange for auto-detected
    const color = regionData.auto_detected === false 
      ? 'rgba(234, 179, 8, 0.4)'  // Yellow for custom
      : 'rgba(251, 146, 60, 0.4)'; // Orange for auto-detected

    regionsRef.current.addRegion({
      id: regionData.id,
      start: regionData.start,
      end: regionData.end,
      color: color,
      drag: true,  // Allow dragging custom regions
      resize: true, // Allow resizing
    });
  };

  const clearPreviewRegions = () => {
    if (!regionsRef.current) return;
    // Remove all preview regions (they have id starting with 'preview-')
    const allRegions = regionsRef.current.getRegions();
    allRegions.forEach(region => {
      if (region.id.startsWith('preview-')) {
        region.remove();
      }
    });
  };

  // Minimum duration for censored regions (in seconds)
  const MIN_REGION_DURATION = 0.3;

  const ensureMinDuration = (start, end) => {
    const duration = end - start;
    if (duration < MIN_REGION_DURATION) {
      return start + MIN_REGION_DURATION;
    }
    return end;
  };

  const showPreviewRegion = (word, isMultiple = false) => {
    if (!regionsRef.current) return;
    
    // Clear any existing preview regions first
    clearPreviewRegions();
    
    // Add preview region with bright green/cyan color to distinguish from actual regions
    const previewColor = isMultiple 
      ? 'rgba(34, 211, 238, 0.5)'  // Cyan for multiple selections
      : 'rgba(74, 222, 128, 0.5)'; // Green for single selection
    
    const regionStart = word.start;
    const rawEnd = word.end || word.start + 0.5;
    const regionEnd = ensureMinDuration(regionStart, rawEnd);
    
    console.log('showPreviewRegion creating region:', { 
      word: word.word, 
      regionStart, 
      regionEnd,
      rawEnd,
      waveformDuration: wavesurferRef.current?.getDuration() 
    });
    
    // Use a very bright, distinct color for preview regions
    const brightPreviewColor = 'rgba(0, 255, 0, 0.7)'; // Bright green with high opacity
    
    regionsRef.current.addRegion({
      id: `preview-${Date.now()}`,
      start: regionStart,
      end: regionEnd,
      color: brightPreviewColor,
      drag: false,
      resize: false,
    });
  };

  const showMultiplePreviewRegions = (words) => {
    if (!regionsRef.current) return;
    
    // Clear any existing preview regions first
    clearPreviewRegions();
    
    // Add preview regions for all matching words
    words.forEach((word, i) => {
      const rawEnd = word.end || word.start + 0.5;
      regionsRef.current.addRegion({
        id: `preview-multi-${Date.now()}-${i}`,
        start: word.start,
        end: ensureMinDuration(word.start, rawEnd),
        color: 'rgba(34, 211, 238, 0.5)', // Cyan for multiple
        drag: false,
        resize: false,
      });
    });
  };

  const handlePlayPause = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const handleSkipBack = () => {
    if (wavesurferRef.current) {
      const newTime = Math.max(0, currentTime - 5);
      wavesurferRef.current.seekTo(newTime / duration);
    }
  };

  const handleSkipForward = () => {
    if (wavesurferRef.current) {
      const newTime = Math.min(duration, currentTime + 5);
      wavesurferRef.current.seekTo(newTime / duration);
    }
  };

  const handleZoomChange = (value) => {
    const zoomValue = value[0];
    setZoom(zoomValue);
    if (wavesurferRef.current) {
      wavesurferRef.current.zoom(zoomValue);
    }
  };

  const handleAudioModeToggle = () => {
    const newMode = audioMode === 'vocal' ? 'original' : 'vocal';
    setAudioMode(newMode);
    
    // Switch audio source
    if (wavesurferRef.current && session) {
      const currentTime = wavesurferRef.current.getCurrentTime();
      const isPlaying = wavesurferRef.current.isPlaying();
      
      const audioUrl = newMode === 'vocal' ? session.files.vocal_url : session.files.original_url;
      wavesurferRef.current.load(audioUrl);
      
      // Restore playback state after load
      wavesurferRef.current.once('ready', () => {
        wavesurferRef.current.seekTo(currentTime / duration);
        if (isPlaying) {
          wavesurferRef.current.play();
        }
      });
    }
  };

  const deleteCurse = async (regionId) => {
    const updatedRegions = regions.filter(r => r.id !== regionId);
    setRegions(updatedRegions);
    
    // Remove from wavesurfer
    if (regionsRef.current) {
      const wsRegions = regionsRef.current.getRegions();
      const region = wsRegions.find(r => r.id === regionId);
      if (region) {
        region.remove();
        console.log('Removed region from waveform:', regionId);
      } else {
        console.warn('Region not found in waveform:', regionId);
        console.log('Available regions:', wsRegions.map(r => r.id));
      }
    }

    try {
      await api.put(`/sessions/${sessionId}`, {
        regions: updatedRegions,
      });
      toast.success('Curse removed');
    } catch (error) {
      toast.error('Failed to remove curse');
      // Revert on error
      fetchProject();
    }
  };

  const seekToRegion = (region) => {
    if (wavesurferRef.current) {
      wavesurferRef.current.seekTo(region.start / duration);
      wavesurferRef.current.play();
    }
  };

  const parseTimeInput = (timeStr) => {
    // Parse "M:SS" or "MM:SS" format to seconds
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      return minutes * 60 + seconds;
    }
    return 0;
  };

  const formatTimeInput = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const handleCreateRegion = async () => {
    if (!newRegionWord.trim()) {
      toast.error('Please enter a word to censor');
      return;
    }

    // Use precise timing from clicked word if available, otherwise parse the input
    const startTime = selectedWordTiming?.start ?? parseTimeInput(newRegionTime);
    const rawEndTime = selectedWordTiming?.end ?? startTime + 0.5;
    const endTime = ensureMinDuration(startTime, rawEndTime);
    
    if (startTime >= duration) {
      toast.error('Time exceeds track duration');
      return;
    }

    const newRegion = {
      id: `custom-${Date.now()}`,
      start: startTime,
      end: endTime,
      word: newRegionWord,
      action: 'silence',
      auto_detected: false,
    };

    const updatedRegions = [...regions, newRegion];
    setRegions(updatedRegions);
    addRegion(newRegion);

    // Seek waveform to the new region so user can see it
    if (wavesurferRef.current && duration > 0) {
      wavesurferRef.current.seekTo(startTime / duration);
    }

    try {
      await api.put(`/sessions/${sessionId}`, {
        regions: updatedRegions,
      });
      toast.success('Custom region created');
      setNewRegionTime('0:00');
      setNewRegionWord('');
      setSelectedWordIndex(-1);
      setSelectedWordTiming(null);
      clearPreviewRegions();
    } catch (error) {
      toast.error('Failed to create region');
      // Revert
      setRegions(regions);
    }
  };

  const handleLyricClick = async (word, index) => {
    console.log('Clicked word:', { word: word.word, start: word.start, end: word.end, index });
    
    // Set the selected word index for visual highlighting
    setSelectedWordIndex(index);
    
    // Store precise timing for when we create the region (with minimum duration)
    const rawEnd = word.end || word.start + 0.5;
    const adjustedEnd = ensureMinDuration(word.start, rawEnd);
    setSelectedWordTiming({ start: word.start, end: adjustedEnd });
    
    // Set the time and word to censor from the clicked lyric (display only)
    setNewRegionTime(formatTimeInput(word.start));
    setNewRegionWord(word.word);
    
    // Seek waveform to the clicked word's position
    if (wavesurferRef.current && duration > 0) {
      const seekRatio = word.start / duration;
      console.log('Seeking to:', { wordStart: word.start, duration, seekRatio });
      wavesurferRef.current.seekTo(seekRatio);
    }
    
    // Show preview region on waveform for the selected word
    console.log('Adding preview region at:', { start: word.start, end: adjustedEnd, rawEnd });
    showPreviewRegion(word);
    
    // Find all other matching instances of this word
    const words = session?.transcription?.words || [];
    const cleanClickedWord = word.word?.toLowerCase().replace(/[^a-z]/gi, '');
    const matchingWords = words.filter((w, i) => {
      const cleanWord = w.word?.toLowerCase().replace(/[^a-z]/gi, '');
      return i !== index && cleanWord === cleanClickedWord;
    });
    
    // If there are other instances, ask if user wants to censor all
    if (matchingWords.length > 0) {
      // First show all matching instances as preview on waveform
      const allMatchingWords = words.filter((w) => {
        const cleanWord = w.word?.toLowerCase().replace(/[^a-z]/gi, '');
        return cleanWord === cleanClickedWord;
      });
      showMultiplePreviewRegions(allMatchingWords);
      
      const censorAll = window.confirm(
        `Found ${matchingWords.length} other instance(s) of "${word.word}" in the song.\n\nWould you like to add ALL instances as regions to censor?`
      );
      
      if (censorAll) {
        const newRegions = allMatchingWords.map((w, i) => {
          const rawEnd = w.end || w.start + 0.5;
          return {
            id: `batch-${Date.now()}-${i}`,
            start: w.start,
            end: ensureMinDuration(w.start, rawEnd),
            word: w.word,
            action: 'silence',
            auto_detected: false,
          };
        });
        
        // Filter out any that already exist as regions
        const existingStarts = new Set(regions.map(r => Math.round(r.start * 100)));
        const uniqueNewRegions = newRegions.filter(r => !existingStarts.has(Math.round(r.start * 100)));
        
        if (uniqueNewRegions.length === 0) {
          toast.info('All instances are already added as regions');
          clearPreviewRegions();
          return;
        }
        
        // Clear preview regions and add actual regions
        clearPreviewRegions();
        
        const updatedRegions = [...regions, ...uniqueNewRegions];
        setRegions(updatedRegions);
        
        // Add visual regions to waveform
        uniqueNewRegions.forEach(region => addRegion(region));
        
        try {
          await api.put(`/sessions/${sessionId}`, {
            regions: updatedRegions,
          });
          toast.success(`Added ${uniqueNewRegions.length} region(s) for "${word.word}"`);
          setNewRegionTime('0:00');
          setNewRegionWord('');
          setSelectedWordIndex(-1);
          setSelectedWordTiming(null);
        } catch (error) {
          toast.error('Failed to add regions');
          setRegions(regions);
        }
      } else {
        // User cancelled, show only the single selected word preview
        showPreviewRegion(word);
      }
    }
  };

  const handleEnhanceLyrics = async (lyricsToUse = null) => {
    if (!sessionId) return;
    
    setIsEnhancing(true);
    try {
      const payload = lyricsToUse ? { custom_lyrics: lyricsToUse } : {};
      const response = await api.post(`/sessions/${sessionId}/enhance-lyrics`, payload);
      setEnhancedLyrics({
        text: response.data.lyricsText,
        confidence: response.data.confidence
      });
      toast.success('Lyrics enhanced');
      // Refetch session to update local state with persisted enhanced data
      fetchProject();
      // Close dialog and reset
      setShowPasteLyricsDialog(false);
      setCustomLyrics('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to enhance lyrics';
      toast.error(message);
    } finally {
      setIsEnhancing(false);
    }
  };

  const updateRegionAction = async (regionId, action) => {
    const updatedRegions = regions.map((r) =>
      r.id === regionId ? { ...r, action } : r
    );
    setRegions(updatedRegions);

    try {
      await api.put(`/sessions/${sessionId}`, {
        regions: updatedRegions,
      });
      toast.success('Region action updated');
    } catch (error) {
      toast.error('Failed to update region');
    }
  };

  const bulkUpdateCensorType = async (censorType) => {
    const updatedRegions = regions.map((r) => ({ ...r, action: censorType }));
    setRegions(updatedRegions);

    try {
      await api.put(`/sessions/${sessionId}`, {
        regions: updatedRegions,
      });
      toast.success(`All curses set to ${censorType}`);
    } catch (error) {
      toast.error('Failed to update regions');
    }
  };

  const updateBeepSettings = async (frequency, volume) => {
    try {
      await api.put(`/sessions/${sessionId}`, {
        beep_frequency: frequency,
        beep_volume: volume / 100, // Convert percentage to 0.0-1.0
      });
      toast.success('Beep settings updated');
    } catch (error) {
      toast.error('Failed to update beep settings');
    }
  };

  const handleExport = async () => {
    if (newRegionWord.trim()) {
      const confirmExport = window.confirm(
        `You have an unsaved region "${newRegionWord}" at ${newRegionTime}. Would you like to add it before exporting?\n\nClick OK to add and export, or Cancel to export without it.`
      );
      if (confirmExport) {
        await handleCreateRegion();
      }
    }
    
    setExporting(true);
    setShowExportModal(true);
    try {
      await api.post(`/sessions/${sessionId}/export`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
      setShowExportModal(false);
    } finally {
      setExporting(false);
    }
  };

  const handleExportComplete = () => {
    const isReprocess = session.files?.clean_url;
    toast.success(isReprocess ? 'Reprocessing complete! Clean version has been updated.' : 'Export complete! Clean version is ready.');
    fetchProject();
  };

  const handleDownload = (url, filename) => {
    if (!url) return;
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download.mp3';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCleanFilename = (title, suffix = '') => {
    // Remove any existing (Clean), (Dirty), (Explicit), (Radio Edit) tags from the title
    let cleanTitle = title
      .replace(/\s*\((Clean|Dirty|Explicit|Radio Edit)\)\s*/gi, '')
      .replace(/\s*-\s*(Clean|Dirty|Explicit)\s*/gi, '')
      .trim();
    // Add the suffix and extension
    return `${cleanTitle}${suffix}.mp3`;
  };

  const hasCurses = regions.length > 0;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1">
      {/* Track Info Header */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="back-button"
              className="h-8 px-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {editingInfo ? (
              <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Song title"
                    className="text-lg font-bold h-9"
                  />
                  <Input
                    value={editArtist}
                    onChange={(e) => setEditArtist(e.target.value)}
                    placeholder="Artist name"
                    className="text-sm h-8"
                  />
                </div>
                <div className="flex gap-1">
                  <Button size="sm" onClick={handleSaveInfo} className="h-8">
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="h-8">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <div>
                  <h1 className="text-2xl font-bold">{session.title || session.filename}</h1>
                  {session.artist && <p className="text-sm text-muted-foreground">{session.artist}</p>}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingInfo(true)}
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Edit song info"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-button"
            size="lg"
            className="w-full sm:w-auto"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {session.files?.clean_url ? 'Reprocess & Export' : 'Process & Export'}
              </>
            )}
          </Button>
        </div>

        {/* Editor Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* Curse List Sidebar */}
          <div className="lg:col-span-1" style={{ height: window.innerWidth >= 1024 ? sidebarHeight : 'auto' }}>
            <Card className="flex flex-col h-full" data-testid="curse-list-sidebar">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Detected Curses ({regions.length})</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewRegionTime(formatTimeInput(currentTime));
                    setNewRegionWord('');
                  }}
                  data-testid="create-region-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Region
                </Button>
              </div>
              
              {/* Bulk Censor Type Change */}
              {regions.length > 0 && (
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => bulkUpdateCensorType('beep')}
                    className="flex-1"
                  >
                    All Beep
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => bulkUpdateCensorType('silence')}
                    className="flex-1"
                  >
                    All Silence
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {/* Create Region Form */}
              <div className="space-y-3 mb-4 p-3 bg-secondary/30 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="region-time" className="text-xs">Time</Label>
                  <Input
                    id="region-time"
                    value={newRegionTime}
                    onChange={(e) => setNewRegionTime(e.target.value)}
                    placeholder="0:00"
                    className="h-8"
                    data-testid="region-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region-word" className="text-xs">Word to Censor</Label>
                  <Input
                    id="region-word"
                    value={newRegionWord}
                    onChange={(e) => setNewRegionWord(e.target.value)}
                    placeholder="Enter word..."
                    className="h-8"
                    data-testid="region-word-input"
                  />
                </div>
                <Button
                  size="sm"
                  className="w-full"
                  onClick={handleCreateRegion}
                  data-testid="add-region-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Region
                </Button>
              </div>

              {/* Beep Settings */}
              <div className="space-y-3 mb-4 p-3 bg-secondary/20 rounded-lg border border-secondary">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">Beep Settings</Label>
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="beep-frequency" className="text-xs">Frequency</Label>
                      <span className="text-xs text-muted-foreground">{beepFrequency}Hz</span>
                    </div>
                    <Select 
                      value={beepFrequency.toString()} 
                      onValueChange={(val) => {
                        const freq = parseInt(val);
                        setBeepFrequency(freq);
                        updateBeepSettings(freq, beepVolume);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="500">Low (500Hz)</SelectItem>
                        <SelectItem value="1000">Medium (1000Hz)</SelectItem>
                        <SelectItem value="1500">High (1500Hz)</SelectItem>
                        <SelectItem value="2000">Very High (2000Hz)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="beep-volume" className="text-xs">Volume</Label>
                      <span className="text-xs text-muted-foreground">{beepVolume}%</span>
                    </div>
                    <Slider
                      id="beep-volume"
                      min={0}
                      max={100}
                      step={5}
                      value={[beepVolume]}
                      onValueChange={([val]) => setBeepVolume(val)}
                      onValueCommit={([val]) => updateBeepSettings(beepFrequency, val)}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1 min-h-[200px]">
                <div className="space-y-2">
                  {regions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No curses detected
                    </p>
                  ) : (
                    regions.map((region, index) => (
                      <div
                        key={region.id}
                        className="p-3 rounded-md border border-border hover:bg-secondary/50 transition-colors"
                        data-testid={`curse-row-${index}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span 
                            className="text-sm font-medium text-primary cursor-pointer hover:underline"
                            onClick={() => seekToRegion(region)}
                          >
                            {formatTime(region.start)}
                          </span>
                          <div className="flex items-center space-x-2">
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{
                                backgroundColor: region.auto_detected === false ? 'rgba(234, 179, 8, 0.2)' : 'rgba(251, 146, 60, 0.2)',
                                borderColor: region.auto_detected === false ? 'rgb(234, 179, 8)' : 'rgb(251, 146, 60)',
                              }}
                            >
                              {region.word}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => deleteCurse(region.id)}
                              data-testid={`delete-curse-${index}`}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                        <Select
                          value={region.action}
                          onValueChange={(value) => updateRegionAction(region.id, value)}
                        >
                          <SelectTrigger className="h-8" data-testid={`curse-action-select-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beep">Beep</SelectItem>
                            <SelectItem value="silence">Silence</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
            </Card>
          </div>

          {/* Waveform and Controls */}
          <div ref={mainContentRef} className="lg:col-span-3 space-y-4">
            {/* Waveform */}
            <Card data-testid="waveform-card">
              <CardContent className="p-6">
                <div
                  ref={waveformRef}
                  className="w-full"
                  data-testid="editor-waveform"
                />
                <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                
                {/* Lyrics Display - Collapsible */}
                {session?.transcription?.words && session.transcription.words.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <Collapsible open={lyricsExpanded} onOpenChange={setLyricsExpanded}>
                      <div className="flex items-center justify-between w-full mb-3">
                        <CollapsibleTrigger className="flex items-center space-x-2 hover:opacity-70 transition-opacity">
                          <Music className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Lyrics</span>
                          <Badge variant="secondary" className="text-xs">
                            {session.transcription.words.length} words
                          </Badge>
                          {(enhancedLyrics || session?.enhanced) && (
                            <Badge variant="outline" className="text-xs text-green-500 border-green-500">
                              Enhanced • {Math.round((enhancedLyrics?.confidence || session?.enhanced?.confidence || 0) * 100)}%
                            </Badge>
                          )}
                          {lyricsExpanded ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </CollapsibleTrigger>
                        {session?.source_lyrics ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEnhanceLyrics();
                            }}
                            disabled={isEnhancing}
                            className="text-xs"
                            title="Attempts to correct the transcription using saved reference lyrics."
                          >
                            {isEnhancing ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Enhancing...
                              </>
                            ) : (
                              <>
                                <Wand2 className="h-3 w-3 mr-1" />
                                Enhance Lyrics (Beta)
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPasteLyricsDialog(true);
                            }}
                            className="text-xs"
                            title="Paste lyrics to improve transcription accuracy"
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Paste Lyrics
                          </Button>
                        )}
                      </div>
                      <CollapsibleContent>
                        {(() => {
                          // Use enhanced words if available (has timestamps), otherwise use transcription
                          const isUsingEnhanced = session?.enhanced?.words && session.enhanced.words.length > 0;
                          const wordsToDisplay = isUsingEnhanced
                            ? session.enhanced.words
                            : session.transcription.words;
                          
                          // If enhanced and we have lyricsText, show formatted view
                          const showFormattedLyrics = isUsingEnhanced && session?.enhanced?.lyricsText;
                          
                          return (
                            <>
                              {isUsingEnhanced && (
                                <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-md">
                                  <p className="text-xs text-green-400 text-center">
                                    <Sparkles className="h-3 w-3 inline mr-1" />
                                    Using enhanced lyrics from reference source ({Math.round((session.enhanced.confidence || 0) * 100)}% match)
                                  </p>
                                </div>
                              )}
                              <ScrollArea 
                                className="h-64"
                                ref={lyricsContainerRef}
                                onMouseEnter={() => setLyricsAutoScroll(false)}
                                onMouseLeave={() => setLyricsAutoScroll(true)}
                              >
                                {showFormattedLyrics ? (
                                  // Show formatted lyrics with line breaks
                                  <div className="text-left leading-relaxed px-2 space-y-2">
                                    {session.enhanced.lyricsText.split('\n').map((line, lineIndex) => {
                                      if (!line.trim()) return <div key={lineIndex} className="h-2" />;
                                      
                                      // Split line into words and find matching timestamps
                                      const lineWords = line.split(/\s+/);
                                      let wordOffset = 0;
                                      
                                      // Calculate starting word index for this line
                                      for (let i = 0; i < lineIndex; i++) {
                                        const prevLine = session.enhanced.lyricsText.split('\n')[i];
                                        if (prevLine.trim()) {
                                          wordOffset += prevLine.split(/\s+/).length;
                                        }
                                      }
                                      
                                      return (
                                        <div key={lineIndex} className="block">
                                          {lineWords.map((wordText, wordIdx) => {
                                            const globalIndex = wordOffset + wordIdx;
                                            const wordData = wordsToDisplay[globalIndex];
                                            
                                            if (!wordData) {
                                              return <span key={wordIdx} className="text-muted-foreground mr-1">{wordText}</span>;
                                            }
                                            
                                            // Check if this word is in a region (must match both timestamp AND word text)
                                            const isRegion = regions.some(region => {
                                              const timeMatch = Math.abs(wordData.start - region.start) < 0.05;
                                              if (!timeMatch) return false;
                                              // Also verify the word text matches (case-insensitive, ignore punctuation)
                                              const cleanWordText = wordText.toLowerCase().replace(/[^a-z']/gi, '');
                                              const cleanRegionWord = (region.word || '').toLowerCase().replace(/[^a-z']/gi, '');
                                              return cleanWordText === cleanRegionWord || cleanRegionWord.includes(cleanWordText) || cleanWordText.includes(cleanRegionWord);
                                            });
                                            
                                            const isSelected = globalIndex === selectedWordIndex;
                                            const isCurrent = wordData.start <= currentTime && wordData.end >= currentTime;
                                            const isMatched = wordData.matched === true;
                                            
                                            return (
                                              <span
                                                key={wordIdx}
                                                ref={isCurrent ? currentWordRef : null}
                                                className={`inline transition-all duration-150 cursor-pointer mr-1 ${
                                                  isCurrent
                                                    ? 'text-white bg-primary/80 px-1.5 py-0.5 rounded font-bold text-lg shadow-lg'
                                                    : isSelected
                                                      ? 'text-yellow-400 bg-yellow-500/30 px-1 rounded font-bold ring-2 ring-yellow-400'
                                                      : isRegion
                                                        ? 'text-orange-400 bg-orange-500/20 px-1 rounded font-medium hover:bg-orange-500/30'
                                                        : isMatched
                                                          ? 'text-green-400 hover:text-green-300'
                                                          : 'text-muted-foreground hover:text-primary'
                                                }`}
                                                onClick={() => handleLyricClick(wordData, globalIndex)}
                                                title={isRegion ? 'Marked for censoring' : isMatched ? 'Matched from reference' : undefined}
                                              >
                                                {wordText}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  // Show word-by-word view (original transcription)
                                  <div className="text-left space-x-1 leading-relaxed px-2">
                                    {wordsToDisplay.map((word, index) => {
                                      const isRegion = regions.some(region => {
                                        const wordStart = word.start;
                                        const timeMatch = Math.abs(wordStart - region.start) < 0.05;
                                        if (!timeMatch) return false;
                                        // Also verify the word text matches (case-insensitive, ignore punctuation)
                                        const cleanWordText = (word.word || '').toLowerCase().replace(/[^a-z']/gi, '');
                                        const cleanRegionWord = (region.word || '').toLowerCase().replace(/[^a-z']/gi, '');
                                        return cleanWordText === cleanRegionWord || cleanRegionWord.includes(cleanWordText) || cleanWordText.includes(cleanRegionWord);
                                      });
                                      
                                      const isSelected = index === selectedWordIndex;
                                      const isCurrent = wordsToDisplay === session.transcription.words 
                                        ? index === currentWordIndex
                                        : (word.start <= currentTime && word.end >= currentTime);
                                      
                                      return (
                                        <span
                                          key={index}
                                          ref={isCurrent ? currentWordRef : null}
                                          className={`inline-block transition-all duration-150 cursor-pointer ${
                                            isCurrent
                                              ? 'text-white bg-primary/80 px-1.5 py-0.5 rounded font-bold text-lg shadow-lg'
                                              : isSelected
                                                ? 'text-yellow-400 bg-yellow-500/30 px-1 rounded font-bold ring-2 ring-yellow-400'
                                                : isRegion
                                                  ? 'text-orange-400 bg-orange-500/20 px-1 rounded font-medium hover:bg-orange-500/30'
                                                  : 'text-muted-foreground hover:text-primary'
                                          }`}
                                          onClick={() => handleLyricClick(word, index)}
                                          data-testid={`lyric-word-${index}`}
                                          title={isRegion ? 'This instance is marked for censoring' : isSelected ? 'Selected word' : undefined}
                                        >
                                          {word.word}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </ScrollArea>
                              <p className="text-xs text-muted-foreground mt-2 text-center">
                                Click any word to select • <span className="text-primary font-bold">Highlighted</span> = current • <span className="text-orange-400">Orange</span> = censored • <span className="text-yellow-400">Yellow</span> = selected
                                {isUsingEnhanced && <> • <span className="text-green-400">Green</span> = matched from reference</>}
                              </p>
                            </>
                          );
                        })()}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transport Controls */}
            <Card data-testid="transport-bar">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  {/* Playback Controls */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSkipBack}
                      data-testid="transport-back-button"
                      className="flex items-center space-x-1"
                    >
                      <SkipBack className="h-4 w-4" />
                      <span className="text-xs">-5</span>
                    </Button>
                    <Button
                      size="sm"
                      onClick={handlePlayPause}
                      data-testid="transport-play-button"
                    >
                      {playing ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSkipForward}
                      data-testid="transport-forward-button"
                      className="flex items-center space-x-1"
                    >
                      <span className="text-xs">+5</span>
                      <SkipForward className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Audio Mode Toggle */}
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="audio-mode" className="text-sm text-muted-foreground">
                      {audioMode === 'vocal' ? 'Acapella' : 'Full Track'}
                    </Label>
                    <Switch
                      id="audio-mode"
                      checked={audioMode === 'original'}
                      onCheckedChange={handleAudioModeToggle}
                      data-testid="audio-mode-toggle"
                    />
                  </div>

                  {/* Zoom Controls */}
                  <div className="flex items-center space-x-2 flex-1 max-w-xs">
                    <ZoomOut className="h-4 w-4 text-muted-foreground" />
                    <Slider
                      value={[zoom]}
                      onValueChange={handleZoomChange}
                      min={0}
                      max={200}
                      step={10}
                      className="flex-1"
                      data-testid="editor-zoom-slider"
                    />
                    <ZoomIn className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">Duration</p>
                    <p className="font-medium">{formatTime(session.duration || 0)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Detected Curses</p>
                    <p className="font-medium">{regions.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">File Type</p>
                    <p className="font-medium">{session.filename?.split('.').pop().toUpperCase()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Download Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Download Options
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="justify-start"
                    disabled={!session.files?.original_url}
                    onClick={() => handleDownload(session.files?.original_url, getCleanFilename(session.title, hasCurses ? ' (Dirty)' : ''))}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Original{hasCurses ? ' (Dirty)' : ''}
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    disabled={!session.files?.instrumental_url}
                    onClick={() => handleDownload(session.files?.instrumental_url, getCleanFilename(session.title, ' (Instrumental)'))}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Instrumental
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    disabled={!session.files?.vocal_url}
                    onClick={() => handleDownload(session.files?.vocal_url, getCleanFilename(session.title, hasCurses ? ' (Dirty Acapella)' : ' (Acapella)'))}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Original Acapella{hasCurses ? ' (Dirty)' : ''}
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    disabled={!session.files?.clean_url}
                    onClick={() => handleDownload(session.files?.clean_url, getCleanFilename(session.title, ' (Clean)'))}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Clean Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    disabled={!session.files?.clean_vocal_url}
                    onClick={() => handleDownload(session.files?.clean_vocal_url, getCleanFilename(session.title, ' (Clean Acapella)'))}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Clean Acapella
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stem Separation Section - Collapsible */}
            {session && session.files && (
              <Collapsible open={stemsExpanded} onOpenChange={setStemsExpanded}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                            <Scissors className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">Stems & Audio to MIDI</CardTitle>
                            <p className="text-sm text-muted-foreground">Extract instrument tracks and convert to MIDI (0.5 min/stem)</p>
                          </div>
                        </div>
                        {stemsExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <StemSeparation
                        sessionId={sessionId}
                        existingStems={session.stems || {}}
                        sessionFiles={session.files}
                        embedded={true}
                        userMinutes={userMinutes}
                        onCreditsUsed={refreshUser}
                      />
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Track Analytics Section - Collapsible (Free) */}
            {session && (
              <Collapsible open={analyticsExpanded} onOpenChange={setAnalyticsExpanded}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                            <BarChart3 className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">Track Analytics</CardTitle>
                            <p className="text-sm text-muted-foreground">BPM, key, chords, melody analysis, and more (0.5 min)</p>
                          </div>
                        </div>
                        {analyticsExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <TrackAnalytics 
                        sessionId={sessionId} 
                        existingAnalysis={session.track_analysis}
                        userMinutes={userMinutes}
                        onCreditsUsed={refreshUser}
                      />
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}
          </div>
        </div>
      </div>

      {/* Export Progress Modal */}
      <ExportProgressModal
        isOpen={showExportModal}
        sessionId={sessionId}
        onClose={() => setShowExportModal(false)}
        onComplete={handleExportComplete}
      />

      {/* Paste Lyrics Dialog */}
      <Dialog open={showPasteLyricsDialog} onOpenChange={setShowPasteLyricsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Paste Lyrics</DialogTitle>
            <DialogDescription>
              Paste the song lyrics below to improve transcription accuracy. The lyrics will be used to correct any transcription errors.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Paste the complete song lyrics here..."
              value={customLyrics}
              onChange={(e) => setCustomLyrics(e.target.value)}
              className="min-h-[200px] resize-none"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {customLyrics.length > 0 ? `${customLyrics.split(/\s+/).filter(w => w).length} words` : 'Tip: Copy lyrics from a lyrics website like Genius or AZLyrics'}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasteLyricsDialog(false);
                setCustomLyrics('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleEnhanceLyrics(customLyrics)}
              disabled={!customLyrics.trim() || isEnhancing}
            >
              {isEnhancing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Enhance Lyrics
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
